import type { components } from "@contracts/generated/editor-service";

import type {
  AdoptionPin,
  ProjectAdoptions,
} from "@/api/library";
import type {
  SummaryLayer,
  SummaryLayerContract,
  SummaryReportSection,
  SummaryStructureRegistry,
  SummaryValueCategory,
} from "@/api/summaryStructure";

/**
 * What a browser may DO with each Summary layer, derived from the server's own
 * document and from nothing else (#640 over #639).
 *
 * ## Why this is a derivation and not a policy
 *
 * The ratified contract's Summary row (§2.2) makes prompt authoring, runtime
 * projection and presentation three separate versioned layers, and #639
 * publishes that model with the one field that decides operability per layer:
 * `writeRoute`, whose contract text is explicit that "Null means no client
 * writes this layer's data directly". So the question "may this surface offer a
 * control here?" has a server-owned answer already, and the whole of this
 * module is reading it rather than restating it.
 *
 * That matters because the alternative is the defect the ticket forbids by
 * name. A browser that decided for itself which Summary fields are editable
 * would be a second copy of the ownership model, free to drift the day the
 * service grows a presentation-state write surface — and free to be WRONG
 * today in the more dangerous direction, offering a control over a protected
 * runtime value. `layerControls` cannot do either: it has no literal to be
 * wrong with.
 *
 * ## What it deliberately does not do
 *
 * It does not decide WHICH controls a layer gets, only whether the layer has a
 * governed write surface at all. It performs no composition, resolves no
 * effective value, and reads no clinical content: the runtime projection's
 * values are protected and are not this module's business, and the prompt
 * bodies belong to the clinical library.
 */

/** Whether a layer has a governed write surface, and where. */
export type SummaryLayerControlState =
  /** `writeRoute` names a route: a client may write this layer THERE. */
  | "server_write_surface"
  /**
   * `writeRoute` is null: no client writes this layer's data directly. The
   * The runtime projection changes only through exact-version adoption and
   * base refresh. The browser derives this state rather than maintaining a
   * second layer policy of its own.
   */
  | "no_write_surface";

export type SummaryLayerControl = SummaryLayerContract & {
  control: SummaryLayerControlState;
};

/**
 * Every layer the registry publishes, in the server's declaration order, with
 * its control state attached.
 *
 * Declaration order is preserved rather than sorted: the contract calls it
 * "The three ratified layers, in declaration order. Never a subset", and the
 * order runs authoring → runtime → presentation, which is the order the layers
 * actually act in. Re-sorting would replace a server-owned sequence with a
 * browser's taste.
 */
export function layerControls(
  registry: SummaryStructureRegistry,
): SummaryLayerControl[] {
  return registry.layers.map((layer) => ({
    ...layer,
    control:
      layer.writeRoute == null ? "no_write_surface" : "server_write_surface",
  }));
}

/**
 * One layer's control coordinates, or `undefined` when the document does not
 * carry it.
 *
 * `undefined` rather than a fabricated default. The contract promises all
 * three layers and never a subset, so an absent one means the response is not
 * the document this build understands — and a surface that invented a
 * `no_write_surface` fallback would render an authoritative-looking claim about
 * ownership that no server said.
 */
export function layerControlFor(
  registry: SummaryStructureRegistry,
  layer: SummaryLayer,
): SummaryLayerControl | undefined {
  return layerControls(registry).find((entry) => entry.layer === layer);
}

/**
 * The value categories a clinical-library family authors, and the ones that are
 * the protected runtime record.
 *
 * Split on the server's `ownership` field, not on the category id: which
 * categories are authorable is #639's projection from the placement template
 * and can widen the day another family gains a Summary placement. A browser
 * that hardcoded `per_key` would keep saying "protected" about a category that
 * had since become authorable.
 */
export function authoredCategories(
  registry: SummaryStructureRegistry,
): SummaryValueCategory[] {
  return registry.valueCategories.filter(
    (category) => category.ownership === "clinician_editable",
  );
}

export function protectedCategories(
  registry: SummaryStructureRegistry,
): SummaryValueCategory[] {
  return registry.valueCategories.filter(
    (category) => category.ownership === "protected_runtime",
  );
}

/**
 * The 12 report sections in the order the PRESENTATION layer declares, which is
 * not necessarily their structural position.
 *
 * Two orders exist in the document and they answer different questions:
 * `position` is the section's structural place in the reference report —
 * "explicit versioned data per ratified §2.1", clinically meaningful — while
 * `presentation.order` is the rendering layer's display order, which the
 * contract seeds equal to position and describes as presentation state. A
 * surface showing the report structure shows the display order, and shows the
 * structural position beside it whenever the two disagree, so that a reorder
 * in Project presentation state can never look like the structural order
 * having moved.
 *
 * `position` breaks ties. Equal `order` values are not something the contract
 * promises against, and a sort that fell back to array index would make the
 * rendered order depend on JSON key order rather than on data.
 */
export function sectionsInDisplayOrder(
  registry: SummaryStructureRegistry,
): SummaryReportSection[] {
  return [...registry.sections].sort(
    (left, right) =>
      left.presentation.order - right.presentation.order ||
      left.position - right.position,
  );
}

/**
 * Whether this section's display order differs from its structural position —
 * the one condition under which both numbers have to be shown.
 */
export function sectionOrderDiffers(section: SummaryReportSection): boolean {
  return section.presentation.order !== section.position;
}

/**
 * The language/direction policy every section shares, or `null` when they do
 * not all agree.
 *
 * The contract carries this per section deliberately — "a consumer of one entry
 * must not need the whole document to learn who owns its language axis" — and
 * v2 declares the same policy for all twelve. A surface that hardcoded that
 * agreement would state one section's ownership over another's the day a
 * registry version differentiates them; a surface that repeated the same
 * sentence twelve times would bury the report structure it exists to show.
 *
 * So the agreement is COMPUTED. `null` is not a failure: it means the sections
 * disagree and the caller must state the policy per row instead of once.
 */
export function uniformLanguageDirection(
  registry: SummaryStructureRegistry,
): SummaryReportSection["languageDirection"] | null {
  const [first, ...rest] = registry.sections;
  if (first === undefined) {
    return null;
  }
  const agreed = rest.every(
    (section) =>
      section.languageDirection.ownership === first.languageDirection.ownership &&
      section.languageDirection.derivedFrom === first.languageDirection.derivedFrom,
  );
  return agreed ? first.languageDirection : null;
}

/**
 * The exact library asset ids the registry's established mappings PROVE, in
 * section order and without duplicates.
 *
 * Only `seededAssetId`, and only where the server put one. #639 is explicit
 * that a recorded pairing whose prompt is base-inherited carries no runtime key
 * and no asset id — "the seven base-inherited reference prompts are never
 * fabricated as library assets" — so a section with a `promptIdentity` and no
 * `seededAssetId` contributes nothing here. Deriving an id from the prompt
 * identity would be inventing exactly the library asset the service refused to
 * invent.
 */
export function mappedAssetIds(
  registry: SummaryStructureRegistry,
): string[] {
  const ids: string[] = [];
  for (const section of sectionsInDisplayOrder(registry)) {
    const id = section.runtimeMapping.seededAssetId;
    if (typeof id === "string" && !ids.includes(id)) {
      ids.push(id);
    }
  }
  return ids;
}

/**
 * The project's pin for one mapped asset, or `undefined`.
 *
 * The join that turns "this section maps to asset X" into "and this project
 * supplies it at exactly vN". It takes the ALREADY-READ pin set rather than
 * fetching one, because the Clinical Summary holds a single
 * `useProjectAdoptionsQuery` and passes it to every view on the page — the
 * arrangement #373 established so that two adoption views cannot disagree
 * about one set, and which a third view must join rather than break.
 *
 * `undefined` distinguishes "this project does not pin the mapped asset" from
 * any version at all, and callers say so in words: an unpinned mapped asset
 * means the section's prompt is supplied by the base pack, which is a fact
 * about supply and not a failure.
 */
export function pinForAsset(
  adoptions: ProjectAdoptions | undefined,
  assetId: string | null | undefined,
): AdoptionPin | undefined {
  if (adoptions === undefined || typeof assetId !== "string") {
    return undefined;
  }
  return adoptions.items.find((pin) => pin.assetId === assetId);
}

/** One exact Summary-prompt pin as the runtime projection reports it (#641). */
export type SummaryPromptRef = components["schemas"]["SummaryPromptRef"];

/**
 * Which library version SUPPLIES one report section's prompt in this project,
 * or the base pack — read off the RUNTIME PROJECTION's own statement of which
 * exact pins placed the files it renders (`summaryPrompts`), never off the pin
 * set alone.
 *
 * WHY THE PIN SET IS NOT ENOUGH. The registry maps a section to a runtime key
 * and, where the seed catalog proves one, to base canon's asset at that key.
 * Asking "does this project pin that seeded asset" answers about ONE row: an
 * authored copy placed at the same key — the editable replacement the library
 * offers — supplies the section and pins a different id, so the old join
 * called the section "the base pack's" while the projection attributed the
 * value to the copy. Composed ownership is the fact: a ref whose `runtimeKey`
 * is the section's key supplies it (an authored pin), and a ref whose
 * `assetId` is the seeded asset supplies it too (base canon's own row, whose
 * ref carries no key by contract). Neither is a placement rule: both are the
 * server's statements, joined on the identities it published.
 *
 * `unread` when the projection has not answered — an unread projection is not
 * evidence that the base pack supplies anything.
 */
export type EffectiveSummarySource =
  | { readonly kind: "unread" }
  | { readonly kind: "unmapped" }
  | { readonly kind: "base" }
  | { readonly kind: "adopted"; readonly assetId: string; readonly version: string };

export function effectiveSummarySource(
  mapping: SummaryReportSection["runtimeMapping"],
  summaryPrompts: readonly SummaryPromptRef[] | undefined,
): EffectiveSummarySource {
  if (mapping.runtimeKey == null && mapping.seededAssetId == null) {
    return { kind: "unmapped" };
  }
  if (summaryPrompts === undefined) {
    return { kind: "unread" };
  }
  const owner =
    summaryPrompts.find(
      (ref) => mapping.runtimeKey != null && ref.runtimeKey === mapping.runtimeKey,
    ) ??
    summaryPrompts.find(
      (ref) => mapping.seededAssetId != null && ref.assetId === mapping.seededAssetId,
    );
  return owner === undefined
    ? { kind: "base" }
    : { kind: "adopted", assetId: owner.assetId, version: owner.version };
}
